import {
  DocumentServicesObject,
  FontStyleParam,
  TpaStyle,
  BuilderFontStyleParam,
} from '@wix/document-services-types';
import {
  convertWixGlobalVariableToLegacyColorKey,
  convertWixThemeVariableToLegacyFontPreset,
  extractWixGlobalVariable,
} from '../../utils/globalVariablesUtils';
import {
  getFontPresetToWixThemeVariableMap,
  getWixThemeVariableToFontPresetMap,
} from '@wix/wix-site-theme-variables';
import { parseFontValue } from '../../../editActionsServices/nativeActions/globalDesign/text/parser';

export type ColorMap = Record<string, { themeName: string; value: string }>;

export const mapGlobalColorVariablesToLegacyValues = ({
  colors,
  documentServicesAPI,
}: {
  documentServicesAPI: DocumentServicesObject;
  colors: ColorMap;
}) => {
  const mappedColors: ColorMap = {};

  for (const [key, value] of Object.entries(colors)) {
    const wixGlobalVariableInValue = extractWixGlobalVariable(value.value);
    const wixGlobalVariableInThemeName = extractWixGlobalVariable(
      value.themeName,
    );

    const wixGlobalVariable =
      wixGlobalVariableInValue || wixGlobalVariableInThemeName;

    if (!wixGlobalVariable) {
      mappedColors[key] = value;
      continue;
    }

    const legacyKey =
      convertWixGlobalVariableToLegacyColorKey(wixGlobalVariable);

    if (!legacyKey) {
      mappedColors[key] = value;
      continue;
    }

    if (wixGlobalVariableInThemeName) {
      mappedColors[key] = {
        value: value.value,
        themeName: legacyKey,
      };
      continue;
    }

    const globalVariablePointer =
      documentServicesAPI.global.variables.getPointerByCssVarName(
        wixGlobalVariable,
      );

    const globalVariableData = globalVariablePointer
      ? documentServicesAPI.global.variables.get(globalVariablePointer)
      : null;

    if (
      !globalVariableData ||
      typeof globalVariableData?.value.value !== 'string'
    ) {
      mappedColors[key] = value;
      continue;
    }

    mappedColors[key] = {
      value: globalVariableData.value.value,
      themeName: legacyKey,
    };
  }

  return mappedColors;
};

type FontMap = TpaStyle['style']['fonts'];

const fontVariableToPresetMap =
  getWixThemeVariableToFontPresetMap() as unknown as Record<string, string>;

const isBuilderFontParam = (
  value: unknown,
): value is BuilderFontStyleParam['param']['value'] => {
  return !!(
    typeof value === 'object' &&
    !!value &&
    'builderFontStyle' in value &&
    value.builderFontStyle
  );
};

const legacyFontPresets = getFontPresetToWixThemeVariableMap();

export const mapFontStyleParamValueToBuilderFontStyle = (
  fontStyleParam: FontStyleParam['param']['value'],
): BuilderFontStyleParam['param']['value'] | null => {
  const newPreset = legacyFontPresets[fontStyleParam.preset ?? ''];
  if (!newPreset) {
    return null;
  }

  const newChange: BuilderFontStyleParam['param']['value'] = {
    builderFontStyle: true,
    cssVarName: newPreset,
    name: '',
    value: newPreset,
    textDecoration: fontStyleParam.style.underline ? 'underline' : 'none',
  };

  return newChange;
};

const mapBuilderFontWithVariableToTpaFont = (
  documentServicesAPI: DocumentServicesObject,
  fontStyle: BuilderFontStyleParam['param']['value'],
) => {
  const varName = fontStyle.cssVarName;
  if (!varName) {
    return;
  }
  const legacyPreset = fontVariableToPresetMap[varName];

  const list = documentServicesAPI.global.variables
    .list({ type: 'FontVar' })
    .map(documentServicesAPI.global.variables.get);
  const variable = list.find((v) => v.cssVarName === varName);

  if (!variable || typeof variable.value.value !== 'string') {
    return;
  }

  const parsedValue = parseFontValue(variable.value.value);
  const tpaFontValue = {
    displayName: variable.name ?? '',
    fontStyleParam: true,
    value: variable.value.value,
    // parsed value is in spx
    size: parsedValue.fontSize ? parseInt(parsedValue.fontSize, 10) : 0,
    family: parsedValue.fontFamily ?? '',
    preset: legacyPreset,
    lineHeight: parsedValue.lineHeight ?? '1em',
    weight: parsedValue.fontWeight ?? 'normal',
    style: {
      bold: Number(parsedValue.fontWeight) >= 700,
      italic: parsedValue.fontStyle === 'italic',
      underline: fontStyle.textDecoration.includes('underline'),
    },
    htmlTag: fontStyle.htmlTag,
  } satisfies FontStyleParam['param']['value'];

  return tpaFontValue;
};

const mapBuilderFontWithValueToTpaFont = (
  fontStyle: BuilderFontStyleParam['param']['value'],
) => {
  try {
    const knownPreset =
      (fontStyle.cssVarName &&
        convertWixThemeVariableToLegacyFontPreset(fontStyle.cssVarName)) ??
      'Custom';
    const fontStringValues = parseFontValue(fontStyle.value);
    const tpaFontParam: FontStyleParam['param']['value'] = {
      fontStyleParam: true,
      displayName: fontStyle.name,
      lineHeight: fontStringValues.lineHeight ?? '1em',
      preset: knownPreset,
      size: parseInt(fontStringValues.fontSize ?? '0', 10),
      family: fontStringValues.fontFamily ?? '',
      weight: fontStringValues.fontWeight ?? 'normal',
      style: {
        bold: Number(fontStringValues.fontWeight) >= 700,
        italic: fontStringValues.fontStyle === 'italic',
        underline: fontStyle.textDecoration.includes('underline'),
      },
      htmlTag: fontStyle.htmlTag,
    };
    return tpaFontParam;
  } catch (error) {
    console.error('Failed to parse builder font value');
    return;
  }
};

export const mapBuilderFontStyleToTpaFontParam = (
  documentServicesAPI: DocumentServicesObject,
  fontStyle: BuilderFontStyleParam['param']['value'],
) => {
  if (fontStyle.cssVarName) {
    const variableMappedValue = mapBuilderFontWithVariableToTpaFont(
      documentServicesAPI,
      fontStyle,
    );
    if (variableMappedValue) {
      return variableMappedValue;
    }
  }
  const valueMappedValue = mapBuilderFontWithValueToTpaFont(fontStyle);
  return valueMappedValue;
};

export const mapGlobalFontVariablesToLegacyValues = ({
  fonts,
  documentServicesAPI,
}: {
  documentServicesAPI: DocumentServicesObject;
  fonts: FontMap;
}) => {
  const mappedFonts: FontMap = {};

  for (const [key, value] of Object.entries(fonts)) {
    if (!isBuilderFontParam(value)) {
      mappedFonts[key] = value;
      continue;
    }
    const variableKey = value.cssVarName;
    if (!variableKey) {
      mappedFonts[key] = value;
      continue;
    }

    const legacyValue = mapBuilderFontWithVariableToTpaFont(
      documentServicesAPI,
      value,
    );
    if (!legacyValue) {
      mappedFonts[key] = value;
      continue;
    }
    mappedFonts[key] = legacyValue;
  }

  return mappedFonts;
};
